import React, { useEffect, useState } from "react"

import {
  FlowAnswer,
  FlowData,
  FlowOutcomeId,
  FlowQuestionId,
  FlowState,
} from "types"
import End from "./End"
import LocalStorage from "./util/local-storage"
import PageWrapper from "../PageWrapper"
import ProgressBar from "./ProgressBar"
import Question from "./Question"
import Sidebar from "./Sidebar"
import Start from "./Start"

export interface FlowProps {
  data: FlowData
  path: string
}

const Flow: React.FC<FlowProps> = ({ data, path }) => {
  const { start } = data
  let firstQuestionId = Object.keys(data.questions)[0] as FlowQuestionId
  let localStorageKeys = {
    answers: "",
    state: "",
  }
  const { nextCallbacks } = data

  const [state, setState] = useState<FlowState>({
    atEndScreen: false,
    currentQuestionId: start ? FlowQuestionId.Start : firstQuestionId,
    previousQuestionId: null,
    nextQuestionId: null,
    inProgress: false,
    outcome: null,
    showContact: false,
    questions: data.questions,
    userAnswers: JSON.parse(
      LocalStorage.getItem(localStorageKeys.answers) || "{}"
    ),
    redirect: null,
  })

  let status = {
    hasCompletedFlow: false,
    hasStartedFlow: false,
    hasScrolledEndPage: false,
  }

  let ids = {
    flowId: data.flowId,
    variantId: data.variantId,
  }

  const idBasis = `${ids.flowId}.${ids.variantId}`

  localStorageKeys.answers = `flow-${idBasis}-answers`
  localStorageKeys.state = `flow-${idBasis}-state`

  function getOutcome() {
    const userAnswers = state.userAnswers
    let outcome: FlowOutcomeId

    switch (userAnswers.mainGoal.value) {
      case FlowAnswer.Lower:
        outcome = FlowOutcomeId.RefiLower
        break
      case FlowAnswer.Cash:
        outcome = FlowOutcomeId.RefiCash
        break
      case FlowAnswer.Faster:
        if (!userAnswers.willPayMore.value) {
          outcome = FlowOutcomeId.GuideFaster
          break
        }

        outcome = FlowOutcomeId.RefiFaster
        break
      case FlowAnswer.Arm:
        if (userAnswers.isStillFixedRate.value === "less") {
          if (userAnswers.willPayMore.value) {
            outcome = FlowOutcomeId.RefiArmShort
            break
          }
          if (userAnswers.movingTimeline.value === "lessOrEqual7") {
            outcome = FlowOutcomeId.RefiArmCheap
            break
          } else {
            outcome = FlowOutcomeId.GuideArm
            break
          }
        }

        outcome = FlowOutcomeId.RefiArmLong
        break
      default:
    }

    return {
      outcome,
      showContact: userAnswers.refinanceReady.value === "yes",
    }
  }

  function saveObjToLocalStorage(key: string, obj: {}) {
    LocalStorage.setItem(key, JSON.stringify(obj))
  }

  function getStateFromLocalStorage() {
    const lsState = JSON.parse(
      LocalStorage.getItem(localStorageKeys.state) || "{}"
    )
    if (Object.keys(lsState).length !== 0) {
      status = lsState
    }
  }

  // Triggers any tracking and sets answers in LS
  const setAnswer = ({
    flowKey,
    value,
    display,
  }: {
    flowKey: FlowQuestionId
    value: string
    display: string
  }) => {
    if (typeof value === "undefined") {
      throw new Error("Cannot set answer an undefined value")
    }

    const userAnswers = state.userAnswers

    userAnswers[flowKey] = { value, display }

    const question = state.questions[flowKey]
    const questionText = question ? question.title : ""
    const answerData = {
      questionId: flowKey,
      question: questionText,
      answer: value,
    }

    saveObjToLocalStorage(localStorageKeys.answers, userAnswers)
    if (state.currentQuestionId !== FlowQuestionId.End) {
      goToNext()
    }

    setState((prevState) => ({
      ...prevState,
      userAnswers,
    }))
  }

  // TODO: Refactor to not just blindly update questions. Should have strict set of allowed update ops.
  function getUpdatedQuestionsState(questionUpdates) {
    let updated

    if (questionUpdates) {
      const currentQuestions = Object.assign({}, state.questions)

      Object.keys(questionUpdates).forEach((questionToUpdate) => {
        Object.keys(questionUpdates[questionToUpdate]).forEach((property) => {
          const qtu = currentQuestions[questionToUpdate]

          if (qtu) {
            qtu[property] = questionUpdates[questionToUpdate][property]
          }
        })
      })

      updated = currentQuestions
    }

    return updated
  }

  function updatePushState(questionId: FlowQuestionId) {
    if (questionId !== null) {
      window.history.pushState(questionId, "", `${path}${questionId}`)
    } else {
      window.history.pushState(FlowQuestionId.Start, "", path)
    }
  }

  const onHistoryChange = (e) => {
    let questionId = start ? FlowQuestionId.Start : firstQuestionId
    let previousQuestionId: FlowQuestionId

    if (e.state !== null) {
      questionId = e.state
    }

    // Basically, if we're not where we're supposed to be...
    if (questionId !== state.currentQuestionId) {
      if (questionId !== FlowQuestionId.Start) {
        if (questionId === FlowQuestionId.End) {
          previousQuestionId = state.currentQuestionId
        } else {
          previousQuestionId = state.questions[questionId].previous
        }
      }

      setState((prevState) => ({
        ...prevState,
        currentQuestionId: questionId,
        previousQuestionId,
      }))
    }
  }

  /**
   * determineQuestionToGoTo -- provide the id & element of the question that will be displayed next
   * @return {object} returns the upcoming question id, upcoming question element, and whether to update the back button
   */
  function determineQuestionToGoTo(isBackButton = false) {
    let questionIdToGoTo: FlowQuestionId
    let questionUpdates = {}
    let gated = false
    let redirect = ""

    // If this is a back button, go to the previous question
    if (isBackButton) {
      questionIdToGoTo = state.questions[state.currentQuestionId].previous
    } else {
      const currentQuestion = state.questions[state.currentQuestionId]

      if (currentQuestion) {
        const nextQuestionId = currentQuestion.next
        if (nextQuestionId) {
          questionIdToGoTo = nextQuestionId
        } else {
          const nextCallbackId = currentQuestion.nextCallback
          if (!nextCallbackId || nextCallbackId === "") {
            throw new Error(
              "Both next and nextCallback are undefined. Cannot load next question"
            )
          }
          if (![nextCallbackId]) {
            throw new Error(
              "nextCallbackId does not correlate to a callback function"
            )
          } else {
            const callbackRet = nextCallbacks[nextCallbackId](state.userAnswers)
            questionIdToGoTo = callbackRet.nextId
            questionUpdates = callbackRet.questionUpdates
            gated = callbackRet.gated
            redirect = callbackRet.redirect
          }
        }
      }
    }

    return {
      questionIdToGoTo,
      questionUpdates,
      gated,
      redirect,
    }
  }

  const startFlow = () => {
    const questionIdToGoTo = firstQuestionId

    const questionUpdates = {}
    questionUpdates[questionIdToGoTo] = {
      previous: FlowQuestionId.Start,
    }

    setState((prevState) => ({
      ...prevState,
      inProgress: true,
      currentQuestionId: questionIdToGoTo,
      previousQuestionId: FlowQuestionId.Start,
      questions: getUpdatedQuestionsState(questionUpdates),
    }))

    getStateFromLocalStorage()
    updatePushState(questionIdToGoTo)

    if (!status.hasStartedFlow) {
      status.hasStartedFlow = true
      saveObjToLocalStorage(localStorageKeys.state, status)
    }
  }

  function checkForEndState(questionId: FlowQuestionId) {
    if (questionId === FlowQuestionId.End) {
      const { outcome, showContact } = getOutcome()

      setState((prevState) => ({
        ...prevState,
        atEndScreen: true,
        inProgress: false,
        outcome,
        showContact,
      }))

      if (!status.hasCompletedFlow) {
        status.hasCompletedFlow = true
        saveObjToLocalStorage(localStorageKeys.state, status)
      }
    }
  }

  function goToNext() {
    const isBackButton = false
    const {
      questionIdToGoTo,
      questionUpdates = {},
      gated = false,
      redirect,
    } = determineQuestionToGoTo(isBackButton)

    if (!gated) {
      checkForEndState(questionIdToGoTo)

      // Update browser history push state
      updatePushState(questionIdToGoTo)

      const currentQuestionId = state.currentQuestionId

      if (questionUpdates[questionIdToGoTo]) {
        questionUpdates[questionIdToGoTo].previous = currentQuestionId
      } else {
        questionUpdates[questionIdToGoTo] = { previous: currentQuestionId }
      }

      setState((prevState) => ({
        ...prevState,
        previousQuestionId: currentQuestionId,
        currentQuestionId: questionIdToGoTo,
        questions: getUpdatedQuestionsState(questionUpdates),
      }))
    } else {
      setState((prevState) => ({
        ...prevState,
        redirect,
        nextQuestionId: questionIdToGoTo,
      }))
    }
  }

  const goToPrevious = () => {
    const isBackButton = true
    const { questionIdToGoTo } = determineQuestionToGoTo(isBackButton)
    let previousQuestionId: FlowQuestionId

    if (questionIdToGoTo === "start") {
      setState((prevState) => ({
        ...prevState,
        inProgress: false,
      }))
    } else {
      previousQuestionId = state.questions[questionIdToGoTo].previous
    }

    updatePushState(questionIdToGoTo)

    setState((prevState) => ({
      ...prevState,
      previousQuestionId,
      currentQuestionId: questionIdToGoTo,
    }))
  }

  const editAnswer = (questionId: FlowQuestionId) => {
    setState((prevState) => ({
      ...prevState,
      currentQuestionId: questionId,
      inProgress: true,
    }))

    updatePushState(questionId)
  }

  const onEmailSubmit = ({
    flowKey,
    value,
    display,
  }: {
    flowKey: FlowQuestionId
    value: string
    display: string
  }) => {
    setAnswer({ flowKey, value, display })
  }

  const onSessionSuccess = (nextQuestionId: FlowQuestionId) => {
    setState((prevState) => ({
      ...prevState,
      currentQuestionId: nextQuestionId,
      previousQuestionId: state.currentQuestionId,
    }))
    checkForEndState(nextQuestionId)
  }

  function onClose() {
    setState((prevState) => ({
      ...prevState,
    }))
  }

  useEffect(() => {
    window.addEventListener("popstate", () => {})
    //TODO fix this not firing
    window.addEventListener("scroll", () => {
      if (
        window.pageYOffset >= window.innerHeight &&
        state.currentQuestionId === FlowQuestionId.End
      ) {
        if (!status.hasScrolledEndPage) {
          status.hasScrolledEndPage = true
          saveObjToLocalStorage(localStorageKeys.state, status)
        }
      }
    })
  }, [])

  const questionKeys = Object.keys(state.questions)

  return (
    <>
      <ProgressBar
        questionKeys={questionKeys}
        currentQuestionId={state.currentQuestionId}
      />

      <PageWrapper>
        {start && (
          <Start
            currentQuestionId={state.currentQuestionId}
            heading={start.title}
            startCallback={startFlow}
            subhead={start.description}
          />
        )}
        {Object.keys(state.questions).map((question) => (
          <Question
            key={question}
            flowKey={question}
            currentQuestionId={state.currentQuestionId}
            previousQuestionId={state.previousQuestionId}
            userAnswer={state.userAnswers[question]}
            onSubmit={setAnswer}
            previousCallback={goToPrevious}
            showBack={
              start !== undefined || (!start && question !== firstQuestionId)
            }
            {...state.questions[question]}
          />
        ))}
        <End
          atEndScreen={state.atEndScreen}
          currentQuestionId={state.currentQuestionId}
          editHandler={editAnswer}
          finalOutcome={state.outcome}
          learnMoreData={data.learnMore}
          outcomes={data.outcomes}
          previousQuestionId={state.previousQuestionId}
          questions={state.questions}
          showContact={state.showContact}
          submitEmailCallback={onEmailSubmit}
          userAnswers={state.userAnswers}
          videos={data.videos}
          vocabulary={data.vocabulary}
        />
        <Sidebar
          currentQuestionId={state.currentQuestionId}
          inProgress={state.inProgress}
        />
      </PageWrapper>
    </>
  )
}
export default Flow
